import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiFetch } from "../../shared/api";
import { apiFieldErrorsMessage } from "../../shared/apiErrors";

// Названия полей товара в формах — для сообщений об ошибках валидации.
const PRODUCT_FIELD_LABELS = {
  name: "Название",
  barcode: "Штрихкод",
  barcodes: "Штрихкоды",
  description: "Описание",
  cost_price: "Стоимость",
  wholesale_price: "Оптовая цена",
  self_cost_price: "Себестоимость",
  category_ids: "Категории",
  is_quick: "Быстрый товар",
  is_active: "Активен",
  show_in_cashback: "Показывать в кэшбэке",
};

function normalizePagedResponse(data) {
  if (Array.isArray(data)) {
    return { items: data, count: data.length };
  }
  const items = Array.isArray(data?.results) ? data.results : [];
  const count = typeof data?.count === "number" ? data.count : items.length;
  return { items, count };
}

export const fetchProducts = createAsyncThunk(
  "products/fetchProducts",
  async ({ search = "", page = 1, pageSize = 25, categoryId = null } = {}) => {
  const qp = new URLSearchParams();
  if (String(search || "").trim()) qp.set("search", String(search || "").trim());
  if (categoryId != null && String(categoryId).trim()) qp.set("category", String(categoryId).trim());
  qp.set("ordering", "name");
  qp.set("page", String(page || 1));
  qp.set("page_size", String(pageSize || 25));
  const path = `/api/product/products/${qp.toString() ? `?${qp.toString()}` : ""}`;
  const data = await apiFetch(path, { method: "GET" });
  return normalizePagedResponse(data);
  }
);

export const fetchCategorySuggestions = createAsyncThunk(
  "products/fetchCategorySuggestions",
  async ({ search = "", pageSize = 20 } = {}) => {
    const qp = new URLSearchParams();
    if (String(search || "").trim()) qp.set("search", String(search || "").trim());
    qp.set("ordering", "name");
    qp.set("page", "1");
    qp.set("page_size", String(pageSize || 20));
    const path = `/api/product/categories/?${qp.toString()}`;
    const data = await apiFetch(path, { method: "GET" });
    return normalizePagedResponse(data);
  }
);

export const fetchProductDetail = createAsyncThunk("products/fetchProductDetail", async ({ productId }) => {
  return await apiFetch(`/api/product/products/${productId}/`, { method: "GET" });
});

export const createProduct = createAsyncThunk("products/createProduct", async ({ body }, { rejectWithValue }) => {
  try {
    const created = await apiFetch("/api/product/products/", { method: "POST", body });
    return created;
  } catch (err) {
    return rejectWithValue(err?.data || { detail: err?.message || "Failed to create product" });
  }
});

export const updateProduct = createAsyncThunk(
  "products/updateProduct",
  async ({ productId, patch }, { rejectWithValue }) => {
    try {
      const updated = await apiFetch(`/api/product/products/${productId}/`, {
        method: "PATCH",
        body: patch,
      });
      return updated;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to update product" });
    }
  }
);

export const deleteProduct = createAsyncThunk(
  "products/deleteProduct",
  async ({ productId }, { rejectWithValue }) => {
    try {
      // Server-side soft delete (sets deleted_at); response is 204 No Content.
      await apiFetch(`/api/product/products/${productId}/`, { method: "DELETE" });
      return { productId };
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to delete product" });
    }
  }
);

export const uploadProductImage = createAsyncThunk(
  "products/uploadProductImage",
  async ({ productId, file, isMain = false }, { rejectWithValue }) => {
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("is_main", isMain ? "true" : "false");
      return await apiFetch(`/api/product/products/${productId}/images/`, { method: "POST", body: fd });
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Upload failed" });
    }
  }
);

export const deleteProductImage = createAsyncThunk(
  "products/deleteProductImage",
  async ({ productId, imageId }, { rejectWithValue }) => {
    try {
      await apiFetch(`/api/product/products/${productId}/images/${imageId}/`, { method: "DELETE" });
      return { productId, imageId };
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Delete failed" });
    }
  }
);

export const setMainProductImage = createAsyncThunk(
  "products/setMainProductImage",
  async ({ productId, imageId }, { rejectWithValue }) => {
    try {
      return await apiFetch(`/api/product/products/${productId}/images/${imageId}/set-main/`, { method: "PATCH" });
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed" });
    }
  }
);

const slice = createSlice({
  name: "products",
  initialState: {
    list: {
      items: [],
      count: 0,
      status: "idle",
      error: null,
      lastSearch: "",
      lastPage: 1,
      lastPageSize: 25,
    },
    categorySuggestions: {
      items: [],
      count: 0,
      status: "idle",
      error: null,
      lastSearch: "",
    },
    byId: {},
    statusById: {},
    errorById: {},
    createStatus: "idle",
    createError: null,
    updateStatusById: {},
    updateErrorById: {},
    deleteStatusById: {},
    deleteErrorById: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state, action) => {
        state.list.status = "loading";
        state.list.error = null;
        state.list.lastSearch = action.meta.arg?.search || "";
        state.list.lastPage = action.meta.arg?.page || 1;
        state.list.lastPageSize = action.meta.arg?.pageSize || 25;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.list.status = "succeeded";
        state.list.items = action.payload?.items || [];
        state.list.count = action.payload?.count ?? state.list.items.length;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.list.status = "failed";
        state.list.error = action.error?.message || "Failed to load products";
      })
      .addCase(fetchCategorySuggestions.pending, (state, action) => {
        state.categorySuggestions.status = "loading";
        state.categorySuggestions.error = null;
        state.categorySuggestions.lastSearch = action.meta.arg?.search || "";
      })
      .addCase(fetchCategorySuggestions.fulfilled, (state, action) => {
        state.categorySuggestions.status = "succeeded";
        state.categorySuggestions.items = action.payload?.items || [];
        state.categorySuggestions.count = action.payload?.count ?? state.categorySuggestions.items.length;
      })
      .addCase(fetchCategorySuggestions.rejected, (state, action) => {
        state.categorySuggestions.status = "failed";
        state.categorySuggestions.error = action.error?.message || "Failed to load categories";
      })
      .addCase(fetchProductDetail.pending, (state, action) => {
        const id = String(action.meta.arg.productId);
        state.statusById[id] = "loading";
        state.errorById[id] = null;
      })
      .addCase(fetchProductDetail.fulfilled, (state, action) => {
        const id = String(action.payload?.id ?? action.meta.arg.productId);
        state.statusById[id] = "succeeded";
        state.byId[id] = action.payload;
      })
      .addCase(fetchProductDetail.rejected, (state, action) => {
        const id = String(action.meta.arg.productId);
        state.statusById[id] = "failed";
        state.errorById[id] = action.error?.message || "Failed to load product";
      })
      .addCase(createProduct.pending, (state) => {
        state.createStatus = "saving";
        state.createError = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.createStatus = "succeeded";
        const created = action.payload;
        if (created?.id != null) {
          const id = String(created.id);
          state.byId[id] = created;
          // best-effort: prepend to list if current list is loaded
          state.list.items = [created, ...(state.list.items || [])];
          state.list.count = Number(state.list.count || 0) + 1;
        }
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.createStatus = "failed";
        const payload = action.payload;
        state.createError = apiFieldErrorsMessage(payload, PRODUCT_FIELD_LABELS, "Не удалось создать товар");
      })
      .addCase(updateProduct.pending, (state, action) => {
        const id = String(action.meta.arg.productId);
        state.updateStatusById[id] = "saving";
        state.updateErrorById[id] = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        const updated = action.payload;
        const id = String(updated?.id ?? action.meta.arg.productId);
        state.updateStatusById[id] = "succeeded";
        if (updated) state.byId[id] = updated;
        state.list.items = (state.list.items || []).map((x) => (x.id === updated?.id ? updated : x));
      })
      .addCase(updateProduct.rejected, (state, action) => {
        const id = String(action.meta.arg.productId);
        state.updateStatusById[id] = "failed";
        const payload = action.payload;
        state.updateErrorById[id] = apiFieldErrorsMessage(payload, PRODUCT_FIELD_LABELS, "Не удалось сохранить товар");
      })
      .addCase(deleteProduct.pending, (state, action) => {
        const id = String(action.meta.arg.productId);
        state.deleteStatusById[id] = "saving";
        state.deleteErrorById[id] = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        const id = String(action.payload.productId);
        state.deleteStatusById[id] = "succeeded";
        // Удалённый товар пропадает из списка и кэша.
        state.list.items = (state.list.items || []).filter((x) => String(x.id) !== id);
        state.list.count = Math.max(0, Number(state.list.count || 0) - 1);
        delete state.byId[id];
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        const id = String(action.meta.arg.productId);
        state.deleteStatusById[id] = "failed";
        const payload = action.payload;
        state.deleteErrorById[id] =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to delete product";
      })
      .addCase(uploadProductImage.fulfilled, (state, action) => {
        const id = String(action.meta.arg.productId);
        const product = state.byId[id];
        if (product) {
          product.images = [...(product.images || []), action.payload];
        }
      })
      .addCase(deleteProductImage.fulfilled, (state, action) => {
        const { productId, imageId } = action.payload;
        const id = String(productId);
        const product = state.byId[id];
        if (product) {
          product.images = (product.images || []).filter((img) => img.id !== Number(imageId));
        }
      })
      .addCase(setMainProductImage.fulfilled, (state, action) => {
        const id = String(action.meta.arg.productId);
        const product = state.byId[id];
        if (product) {
          product.images = (product.images || []).map((img) => ({
            ...img,
            is_main: img.id === action.payload.id,
          }));
        }
      });
  },
});

export default slice.reducer;

export const selectProducts = (state) => state.products.list.items || [];
export const selectProductsCount = (state) => Number(state.products.list.count || 0);
export const selectProductsStatus = (state) => state.products.list.status || "idle";
export const selectProductsError = (state) => state.products.list.error || null;

export const selectCategorySuggestions = (state) => state.products.categorySuggestions.items || [];
export const selectCategorySuggestionsStatus = (state) => state.products.categorySuggestions.status || "idle";
export const selectCategorySuggestionsError = (state) => state.products.categorySuggestions.error || null;

export const selectCreateProductStatus = (state) => state.products.createStatus || "idle";
export const selectCreateProductError = (state) => state.products.createError || null;

export const selectProductById = (id) => (state) => state.products.byId[String(id)] || null;
export const selectProductStatusById = (id) => (state) => state.products.statusById[String(id)] || "idle";
export const selectProductErrorById = (id) => (state) => state.products.errorById[String(id)] || null;

export const selectUpdateProductStatusById = (id) => (state) => state.products.updateStatusById[String(id)] || "idle";
export const selectUpdateProductErrorById = (id) => (state) => state.products.updateErrorById[String(id)] || null;

export const selectDeleteProductStatusById = (id) => (state) => state.products.deleteStatusById[String(id)] || "idle";
export const selectDeleteProductErrorById = (id) => (state) => state.products.deleteErrorById[String(id)] || null;

